import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import Page, { PageButtonLink } from '../../../../components/Page';
import Card from '../../../../components/Card';
import ListMessage from './ListMessage';
import { LoadingOverlay } from '../../../../components/Loading';
import Filters from './Filters';
import { Table, Thead, Tr, LinkTr, Th, Tbody, Td } from '../../../../components/Table/Fake';
import Icon from '../../../../components/Icons';
import { H1 } from '../../../../components/Typography';
import { useAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { distances } from '../../../../styles/constants';
import { SalesLocation } from '../../../../types/customers';
import { useActions } from '../../../../Actions';

import * as selectors from '../../selectors';
import Paging from '../../../../components/Paging/Infinite';
import { SalesLocationQuery } from '../../locationActions';

const LocationRow = (
    {
        location,
    }: {
        location: SalesLocation;
    }
) => (<LinkTr to={`/${location.account_id}/settings/locations/${location.location_id}`}>
    <Td className="edge" />
    <Td className="first-child">{location.name}</Td>
    <Td className="ellipsis">{location.location_id}</Td>
    <Td>{location.address?.address_line}</Td>
    <Td>{location.address?.postal_place}</Td>
    <Td>{`${location?.email ?? ''} ${location?.phone_number ?? ''}`}</Td>
    <Td>{location.status}</Td>
    <Td className="edge" />
</LinkTr>);

export type LocationTableProps = {
    locations: SalesLocation[];
    title: string;
    more?: boolean;
    loading?: boolean;
    onLoadMore: () => void;
}

const LocationTable = (
    {
        locations,
        title,
        more,
        loading,
        onLoadMore,
    }: LocationTableProps
) => {
    const { t } = useTranslation('app');
    return (
        <MarginTopCard noPadding title={title}>
            <Table className="no-wrap">
                <Thead>
                    <Tr>
                        <Th className="edge" />
                        <Th className="first-child">{t('locations.locations_list.fields.name')}</Th>
                        <Th>{t('locations.locations_list.fields.store_id')}</Th>
                        <Th>{t('locations.locations_list.fields.address')}</Th>
                        <Th>{t('locations.locations_list.fields.city')}</Th>
                        <Th>{t('locations.locations_list.fields.contact')}</Th>
                        <Th className="last-child">{t('locations.locations_list.fields.status')}</Th>
                        <Th className="edge" />
                    </Tr>
                </Thead>
                <Tbody>
                    {locations.map((location, index) => (
                        <LocationRow location={location} key={index} />
                    ))}
                </Tbody>
            </Table>
            <Paging
                label={t('locations.locations_list.load_more')}
                hasMore={!!more}
                isLoading={!!loading}
                onLoadMore={onLoadMore}
            />
            {loading &&
                <LoadingOverlay />
            }
        </MarginTopCard>
    );
};

const selector = createSelector(
    selectors.hasMore,
    selectors.isLoading,
    selectors.isLoadingAny,
    selectors.salesLocations,
    selectors.query,
    selectors.startAfter,
    (more, isLoading, loadingAny, locations, query, startAfter) => ({ more, isLoading, loadingAny, locations, query, startAfter })
);

const initialQuery: SalesLocationQuery = {
    limit: 100,
};

const LocationsList = () => {
    const getLocations = useActions('settings.locations').getLocations;
    const { t } = useTranslation('app');
    const {
        more,
        isLoading,
        loadingAny,
        locations,
        query,
        startAfter,
    } = useSelector(selector);

    const {
        accountIds,
        urlAccountId,
    } = useAccountIds();

    useEffect(() => {
        accountIds.forEach(aid => getLocations(initialQuery, aid));
    }, [accountIds, getLocations]);

    const onLoadMore = (accountId: string) => () => {
        if (more(accountId)) {
            getLocations({
                ...query(accountId),
                starting_after: startAfter(accountId),
            }, accountId);
        }
    };

    const onFilter = (filter: Partial<SalesLocationQuery>) => accountIds.forEach(aid => {
        getLocations({
            ...initialQuery,
            ...filter,
            starting_after: undefined,
        }, aid);
    });

    const prodAccount = accountIds.find((x) => x.startsWith('P'));
    const hasProdLocations = prodAccount && (locations[prodAccount] || []).length > 0;

    const testAccount = accountIds.find((x) => x.startsWith('T'));
    const hasTestLocations = testAccount && (locations[testAccount] || []).length > 0;

    return (
        <Page
            title={
                <>
                    <Title>{t('locations.locations_list.title')}</Title>
                    <PageButtonLink to={`/${urlAccountId}/settings/locations/new`}>
                        <Icon icon="document" />
                        {t('locations.locations_list.add_location_button')}
                    </PageButtonLink>
                </>
            }
        >
            <Card noPadding>
                <Filters onChange={onFilter} />
            </Card>
            {hasProdLocations && (
                <LocationTable
                    locations={locations[prodAccount] || []}
                    title={t('environments.production')}
                    more={more(prodAccount)}
                    onLoadMore={onLoadMore(prodAccount)}
                    loading={isLoading(prodAccount)}
                />
            )}
            {hasTestLocations && (
                <LocationTable
                    locations={locations[testAccount] || []}
                    title={t('environments.test')}
                    more={more(testAccount)}
                    onLoadMore={onLoadMore(testAccount)}
                    loading={isLoading(testAccount)}
                />
            )}
            {!hasProdLocations && !hasTestLocations && (
                <MarginTopCard>
                    <ListMessage
                        locations={Object.values(locations).flat()}
                        isLoading={loadingAny}
                    />
                </MarginTopCard>
            )}
        </Page>
    );
};


const MarginTopCard = styled(Card)`
    margin-top: ${distances.small};
`;

const Title = styled(H1 as any)`
    display: inline-block;
`;

export default LocationsList;
